// src/pages/ProfileEdit.tsx
// Страница редактирования профиля
// v3 fixes:
//   - Added validation for required fields (first_name, last_name, phone_number cannot be empty)
//   - Removed ability to save empty values

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import {
    COUNTRIES,
    validatePhoneNumber,
    formatPhoneNumber,
    getPhoneExample,
    PHONE_CODES,
} from '../utils/phoneUtils';
import { validateEmail } from '../utils/validation';

interface FormData {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    phone_country_code: string;
    country_code: string;
}

const DEFAULT_FORM: FormData = {
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    phone_country_code: '+992',
    country_code: 'TJ',
};

function userToForm(user: NonNullable<ReturnType<typeof useAuth>['user']>): FormData {
    return {
        first_name: user.first_name ?? '',
        last_name: user.last_name ?? '',
        email: user.email ?? '',
        phone_number: user.phone_number ?? '',
        phone_country_code: user.phone_country_code ?? '+992',
        country_code: user.country_code ?? 'TJ',
    };
}

export const ProfileEdit: React.FC = () => {
    const navigate = useNavigate();
    const { user, updateProfile, initiateEmailChange, resendConfirmationEmail, pendingEmailChange } = useAuth();

    // UI state
    const [saving, setSaving] = useState(false);
    const [sendingEmailChange, setSendingEmailChange] = useState(false);
    const [resendingEmail, setResendingEmail] = useState(false);

    // Form state
    const [formData, setFormData] = useState<FormData>(DEFAULT_FORM);
    const [originalData, setOriginalData] = useState<FormData>(DEFAULT_FORM);

    // Validation errors
    const [firstNameError, setFirstNameError] = useState('');
    const [lastNameError, setLastNameError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [emailError, setEmailError] = useState('');

    // Local pending email
    const [pendingEmail, setPendingEmail] = useState<string | null>(pendingEmailChange);

    // formReady: true after first successful population from user data
    const formReadyRef = useRef(false);
    // Track previous email_confirmed to detect confirmation event
    const prevEmailConfirmedRef = useRef<boolean | null>(null);

    // ── Populate form from user ───────────────────────────────────────────────

    useEffect(() => {
        if (!user) {
            navigate('/profile');
            return;
        }

        // First load — populate form and originalData
        if (!formReadyRef.current) {
            formReadyRef.current = true;
            prevEmailConfirmedRef.current = user.email_confirmed;
            const data = userToForm(user);
            setFormData(data);
            setOriginalData(data);
            // Sync local pendingEmail from context
            setPendingEmail(pendingEmailChange);
            return;
        }

        // Subsequent user updates — only react to email confirmation event.
        const wasUnconfirmed = prevEmailConfirmedRef.current === false;
        const nowConfirmed = user.email_confirmed === true;

        if (wasUnconfirmed && nowConfirmed) {
            // Email change was confirmed — update form + originalData with new email
            prevEmailConfirmedRef.current = true;
            const data = userToForm(user);
            setFormData(data);
            setOriginalData(data);
            setPendingEmail(null);
            toast.success('Email успешно подтвержден и обновлен!');
        }

        // Always track latest value for next comparison
        prevEmailConfirmedRef.current = user.email_confirmed;

    }, [user, navigate, pendingEmailChange]);

    // ── Derived state ─────────────────────────────────────────────────────────

    const hasProfileChanges =
        formData.first_name !== originalData.first_name ||
        formData.last_name !== originalData.last_name ||
        formData.country_code !== originalData.country_code ||
        formData.phone_number !== originalData.phone_number ||
        formData.phone_country_code !== originalData.phone_country_code;

    const hasEmailChanged = formData.email !== originalData.email;
    const emailChangePending = pendingEmail !== null;

    // ── Validation functions ──────────────────────────────────────────────────

    const validateFirstName = (value: string): boolean => {
        const trimmed = value.trim();
        if (!trimmed) {
            setFirstNameError('Имя не может быть пустым');
            return false;
        }
        if (trimmed.length < 2) {
            setFirstNameError('Имя должно содержать минимум 2 символа');
            return false;
        }
        setFirstNameError('');
        return true;
    };

    const validateLastName = (value: string): boolean => {
        const trimmed = value.trim();
        if (!trimmed) {
            setLastNameError('Фамилия не может быть пустой');
            return false;
        }
        if (trimmed.length < 2) {
            setLastNameError('Фамилия должна содержать минимум 2 символа');
            return false;
        }
        setLastNameError('');
        return true;
    };

    const validatePhone = (phone: string, code: string): boolean => {
        const clean = phone.replace(/\D/g, '');
        if (!clean) {
            setPhoneError('Номер телефона не может быть пустым');
            return false;
        }
        if (!validatePhoneNumber(clean, code)) {
            setPhoneError(`Неверный формат. Пример: ${getPhoneExample(code)}`);
            return false;
        }
        setPhoneError('');
        return true;
    };

    // ── Field handlers ────────────────────────────────────────────────────────

    const handleFieldChange = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        // Real-time validation
        if (field === 'first_name') {
            validateFirstName(value);
        } else if (field === 'last_name') {
            validateLastName(value);
        }
    };

    const handlePhoneChange = (value: string) => {
        const formatted = formatPhoneNumber(value, formData.phone_country_code);
        setFormData((prev) => ({ ...prev, phone_number: formatted }));
        validatePhone(formatted, formData.phone_country_code);
    };

    const handlePhoneCodeChange = (code: string) => {
        setFormData((prev) => ({ ...prev, phone_country_code: code }));
        validatePhone(formData.phone_number, code);
    };

    const handleEmailInput = (value: string) => {
        setFormData((prev) => ({ ...prev, email: value }));
        setEmailError(
            value && !validateEmail(value) ? 'Введите корректный email адрес' : ''
        );
    };

    // ── Email change ──────────────────────────────────────────────────────────

    const handleInitiateEmailChange = async () => {
        if (!validateEmail(formData.email)) {
            toast.error('Введите корректный email адрес');
            return;
        }

        const targetEmail = formData.email;
        setSendingEmailChange(true);
        const result = await initiateEmailChange(targetEmail);
        setSendingEmailChange(false);

        if (!result.success) {
            toast.error(result.error ?? 'Ошибка при смене email');
            return;
        }

        // Reset email field to current email — the new one is "pending"
        setFormData((prev) => ({ ...prev, email: originalData.email }));
        setPendingEmail(targetEmail);
        toast.success(`Письмо отправлено на ${targetEmail}`);
    };

    const handleResendEmailChange = async () => {
        if (!pendingEmail) return;
        setResendingEmail(true);
        const result = await initiateEmailChange(pendingEmail);
        setResendingEmail(false);
        if (result.success) {
            toast.success(`Письмо повторно отправлено на ${pendingEmail}`);
        } else {
            toast.error(result.error ?? 'Ошибка при повторной отправке');
        }
    };

    // ── Save profile ──────────────────────────────────────────────────────────

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate required fields before saving
        const isFirstNameValid = validateFirstName(formData.first_name);
        const isLastNameValid = validateLastName(formData.last_name);
        const isPhoneValid = validatePhone(formData.phone_number, formData.phone_country_code);

        if (!isFirstNameValid || !isLastNameValid || !isPhoneValid) {
            toast.error('Пожалуйста, заполните все обязательные поля корректно');
            return;
        }

        // Check if there are any changes to save
        if (!hasProfileChanges) {
            toast('Нет изменений для сохранения');
            navigate('/profile');
            return;
        }

        setSaving(true);

        try {
            // Build patch with only changed fields
            const patch: Record<string, string> = {};

            if (formData.first_name !== originalData.first_name)
                patch.first_name = formData.first_name.trim();
            if (formData.last_name !== originalData.last_name)
                patch.last_name = formData.last_name.trim();
            if (formData.country_code !== originalData.country_code)
                patch.country_code = formData.country_code;
            if (formData.phone_country_code !== originalData.phone_country_code)
                patch.phone_country_code = formData.phone_country_code;
            if (formData.phone_number !== originalData.phone_number) {
                const cleanPhone = formData.phone_number.replace(/\D/g, '');
                patch.phone_number = cleanPhone;
            }

            const result = await updateProfile(patch as any);

            if (!result.success) {
                toast.error(result.error ?? 'Ошибка обновления профиля');
                return;
            }

            // Sync originalData so form no longer shows unsaved-changes state
            setOriginalData((prev) => ({ ...prev, ...patch }));

            toast.success('Профиль успешно обновлен');
            setTimeout(() => navigate('/profile'), 500);

        } catch (error) {
            console.error('Save error:', error);
            toast.error('Ошибка при сохранении профиля');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => navigate('/profile');

    // ── Guard ─────────────────────────────────────────────────────────────────

    if (!user) return null;

    // Check if save button should be disabled
    const isSaveDisabled = saving || !hasProfileChanges || !!firstNameError || !!lastNameError || !!phoneError;

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="profile-container-mobile">
            <div className="profile-header-mobile profile-edit">
                <div className="form-header">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="back-button"
                        disabled={saving}
                    >
                        <ArrowLeftIcon style={{ width: 18, height: 18 }} /> Назад
                    </button>
                    <h3>Редактирование профиля</h3>
                </div>
            </div>

            <form onSubmit={handleSave} className="profile-form-mobile">

                {/* First name - REQUIRED */}
                <div className="form-group-mobile">
                    <label>Имя <span className="required-star">*</span></label>
                    <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => handleFieldChange('first_name', e.target.value)}
                        placeholder="Введите ваше имя"
                        disabled={saving}
                        className={firstNameError ? 'error' : ''}
                    />
                    {firstNameError && <div className="error-message">{firstNameError}</div>}
                </div>

                {/* Last name - REQUIRED */}
                <div className="form-group-mobile">
                    <label>Фамилия <span className="required-star">*</span></label>
                    <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => handleFieldChange('last_name', e.target.value)}
                        placeholder="Введите вашу фамилию"
                        disabled={saving}
                        className={lastNameError ? 'error' : ''}
                    />
                    {lastNameError && <div className="error-message">{lastNameError}</div>}
                </div>

                {/* Email - OPTIONAL but validated if provided */}
                <div className="form-group-mobile">
                    <label>Email</label>
                    <div className="email-input-with-status">
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleEmailInput(e.target.value)}
                            placeholder="your@email.com"
                            disabled={saving || emailChangePending}
                            className={emailError ? 'error' : ''}
                        />

                        {hasEmailChanged && !emailChangePending && (
                            <button
                                type="button"
                                onClick={handleInitiateEmailChange}
                                className="verify-email-button"
                                disabled={sendingEmailChange || !!emailError}
                            >
                                {sendingEmailChange ? 'Отправка...' : 'Подтвердить и сменить'}
                            </button>
                        )}

                        {emailChangePending && (
                            <button
                                type="button"
                                onClick={handleResendEmailChange}
                                className="verify-email-button resend"
                                disabled={resendingEmail}
                            >
                                {resendingEmail ? 'Отправка...' : '↻ Отправить повторно'}
                            </button>
                        )}
                    </div>

                    {emailError && <div className="error-message">{emailError}</div>}

                    {emailChangePending && pendingEmail && (
                        <div className="field-note warning">
                            ✉️ Письмо отправлено на <strong>{pendingEmail}</strong>.
                            После подтверждения по ссылке email обновится автоматически.
                        </div>
                    )}

                    {!emailChangePending && user.email_confirmed && !hasEmailChanged && (
                        <div className="field-note success">✅ Email подтвержден</div>
                    )}

                    {!emailChangePending && !user.email_confirmed && !hasEmailChanged && (
                        <div className="field-note warning">
                            ⚠️ Email не подтвержден.{' '}
                            <button
                                type="button"
                                onClick={() => resendConfirmationEmail(formData.email)}
                                className="resend-confirm-link"
                            >
                                Отправить письмо повторно
                            </button>
                        </div>
                    )}
                </div>

                {/* Country */}
                <div className="form-group-mobile">
                    <label>Страна доставки</label>
                    <select
                        value={formData.country_code}
                        onChange={(e) => handleFieldChange('country_code', e.target.value)}
                        disabled={saving}
                        className="auth-input"
                    >
                        {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>
                                {c.flag} {c.name}
                            </option>
                        ))}
                    </select>
                    <div className="field-note">Влияет на стоимость доставки</div>
                </div>

                {/* Phone - REQUIRED */}
                <div className="form-group-mobile">
                    <label>Номер телефона <span className="required-star">*</span></label>
                    <div className="phone-input-wrapper">
                        <select
                            value={formData.phone_country_code}
                            onChange={(e) => handlePhoneCodeChange(e.target.value)}
                            disabled={saving}
                            className="phone-code-select"
                        >
                            {PHONE_CODES.map((item) => (
                                <option key={item.code} value={item.code}>
                                    {item.flag} {item.code}
                                </option>
                            ))}
                        </select>
                        <input
                            type="tel"
                            value={formData.phone_number}
                            onChange={(e) => handlePhoneChange(e.target.value)}
                            placeholder={getPhoneExample(formData.phone_country_code)}
                            disabled={saving}
                            className={`phone-input-field ${phoneError ? 'error' : ''}`}
                        />
                    </div>
                    {phoneError && <div className="error-message">{phoneError}</div>}
                    <div className="field-note">
                        Код страны телефона не зависит от страны доставки
                    </div>
                </div>

                {/* Actions */}
                <div className="profile-actions">
                    <button
                        type="submit"
                        className="save-button"
                        disabled={isSaveDisabled}
                    >
                        {saving ? 'Сохранение...' : 'Сохранить изменения'}
                    </button>
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="cancel-button"
                        disabled={saving}
                    >
                        Отмена
                    </button>
                </div>
            </form>
        </div>
    );
};